import jsQR from 'jsqr';

/**
 * QUEM LÊ O QR DO QUADRO DA CÂMERA — e por que são dois caminhos.
 *
 * ## O nativo quando há, a biblioteca quando não há
 *
 * O Chrome e o Android têm `BarcodeDetector`: leitura no próprio navegador,
 * rápida e sem custo de bateria relevante. O Safari do iPhone NÃO tem — e o
 * app do dono é instalado pela tela de início do celular (decisão 49), então
 * iPhone não é caso de borda, é metade do campo.
 *
 * Por isso a biblioteca (`jsqr`) entra como queda, e ela vai EMBUTIDA no
 * pacote, nunca de um endereço externo: na obra não há internet, e um leitor
 * que precisa baixar algo para funcionar não funciona justamente onde o
 * adesivo está colado.
 *
 * ## O quadro é reduzido antes de ler, e isso é desempenho e não preguiça
 *
 * A câmera entrega até 4K. Procurar o código em 8 milhões de pixels, trinta
 * vezes por segundo, esquenta o aparelho e derruba a taxa de quadros — e o
 * vídeo travado é o que faz a pessoa achar que o leitor não funciona. A
 * leitura acontece num quadro reduzido: um QR que ocupa um quarto da tela
 * continua com módulos de sobra em 640 px.
 */

/** O lado máximo do quadro lido. Acima disto é custo sem ganho de leitura. */
const LADO_DA_LEITURA = 640;

/* `BarcodeDetector` ainda não está na biblioteca de tipos do TypeScript. A
   declaração é mínima de propósito: só o que este arquivo usa. */
interface CodigoDetectado {
  readonly rawValue: string;
}
interface DetectorDeCodigo {
  detect: (fonte: CanvasImageSource) => Promise<readonly CodigoDetectado[]>;
}
interface FabricaDeDetector {
  new (opcoes: { formats: readonly string[] }): DetectorDeCodigo;
  getSupportedFormats?: () => Promise<readonly string[]>;
}

function fabricaNativa(): FabricaDeDetector | null {
  const janela = globalThis as unknown as { BarcodeDetector?: FabricaDeDetector };
  return janela.BarcodeDetector ?? null;
}

/** Um leitor pronto para receber quadros. `ler` devolve o texto, ou `null`. */
export interface LeitorDeQuadros {
  readonly ler: (video: HTMLVideoElement) => Promise<string | null>;
  /** Qual caminho está em uso. Vai para a tela de diagnóstico, não para o usuário. */
  readonly motor: 'navegador' | 'biblioteca';
}

/**
 * Abre o leitor, escolhendo o motor uma vez só.
 *
 * A escolha é feita ANTES do primeiro quadro, e não a cada um: perguntar ao
 * navegador se ele tem o detector trinta vezes por segundo é trabalho puro.
 * E `getSupportedFormats` é consultado porque existe navegador que tem a
 * classe e não lê QR — ter o objeto não é ter o formato.
 */
export async function abrirLeitorDeQr(): Promise<LeitorDeQuadros> {
  const Fabrica = fabricaNativa();
  if (Fabrica !== null) {
    try {
      const formatos = (await Fabrica.getSupportedFormats?.()) ?? ['qr_code'];
      if (formatos.includes('qr_code')) {
        const detector = new Fabrica({ formats: ['qr_code'] });
        return {
          motor: 'navegador',
          ler: async (video) => {
            const achados = await detector.detect(video);
            return achados[0]?.rawValue ?? null;
          },
        };
      }
    } catch {
      /* Navegador com a classe presente e quebrada: cai para a biblioteca, que
         é o caminho que sempre funciona. */
    }
  }

  const tela = document.createElement('canvas');
  const pincel = tela.getContext('2d', { willReadFrequently: true });
  return {
    motor: 'biblioteca',
    ler: (video) => Promise.resolve(lerComBiblioteca(video, tela, pincel)),
  };
}

function lerComBiblioteca(
  video: HTMLVideoElement,
  tela: HTMLCanvasElement,
  pincel: CanvasRenderingContext2D | null,
): string | null {
  if (pincel === null || video.videoWidth === 0) return null;
  const escala = Math.min(1, LADO_DA_LEITURA / Math.max(video.videoWidth, video.videoHeight));
  tela.width = Math.round(video.videoWidth * escala);
  tela.height = Math.round(video.videoHeight * escala);
  pincel.drawImage(video, 0, 0, tela.width, tela.height);
  const imagem = pincel.getImageData(0, 0, tela.width, tela.height);
  /* `attemptBoth` lê o código claro sobre escuro também: adesivo fotografado
     contra o sol volta invertido com mais frequência do que parece. */
  const achado = jsQR(imagem.data, imagem.width, imagem.height, { inversionAttempts: 'attemptBoth' });
  return achado?.data ?? null;
}

/**
 * O QR de uma FOTO, e não do vídeo.
 *
 * É a queda para o aparelho onde a câmera ao vivo não abre — o app aberto num
 * computador sem webcam, o navegador que negou a permissão de vídeo mas deixa
 * tirar foto, o iPhone de quem recusou o acesso uma vez e não achou onde
 * desfazer. A câmera do próprio sistema operacional tira a foto, e ela volta
 * para cá.
 *
 * Aqui o quadro NÃO é reduzido: a foto é um quadro só, o custo não se repete,
 * e a resolução inteira é o que salva a leitura de um adesivo pequeno na
 * imagem. É o contrário da escolha do vídeo, e pelo mesmo motivo — ler no
 * tamanho que der mais chance, pelo tempo que se pode gastar.
 */
export async function lerQrDeImagem(arquivo: Blob): Promise<string | null> {
  const imagem = await createImageBitmap(arquivo).catch(() => null);
  if (imagem === null) return null;
  try {
    const tela = document.createElement('canvas');
    tela.width = imagem.width;
    tela.height = imagem.height;
    const pincel = tela.getContext('2d', { willReadFrequently: true });
    if (pincel === null) return null;
    pincel.drawImage(imagem, 0, 0);
    const dados = pincel.getImageData(0, 0, tela.width, tela.height);
    return jsQR(dados.data, dados.width, dados.height, { inversionAttempts: 'attemptBoth' })?.data ?? null;
  } finally {
    imagem.close();
  }
}
